import {HttpResponse} from '@angular/common/http';

// Regex for RFC 5987 encoded filenames (filename*)
// - `filename\*=`: Exactly matches "filename*=" (RFC 5987 encoding)
// - `utf-8''`: Matches the charset and language components (typically utf-8 with no language)
// - `([^;"\r\n]+)`: Captures the actual filename (everything until a semicolon, quote, or line ending)
const FILENAME_EXTRACTION_REGEX_RFC5987 = /filename\*=utf-8''([^;"\r\n]+)/i;

// Regex for regular filename attribute
// - `filename=`: Exactly matches "filename="
// - `["']?`: Optionally matches a single or double quote (filename can be quoted or unquoted)
// - `([^"';\r\n]+)`: Captures the filename (everything until a quote, semicolon, or line ending)
// - `["']?`: Optionally matches the closing quote if one was used
const FILENAME_EXTRACTION_REGEX_STANDARD = /filename=["']?([^"';\r\n]+)["']?/i;

/**
 * Extracts the filename from the Content-Disposition header.
 * Prioritizes filename* (RFC 5987) for better character encoding support,
 * then falls back to filename.
 *
 * @param response The HTTP response containing Content-Disposition header
 * @param defaultVal Default value to return if filename can't be extracted
 * @returns The extracted filename, or defaultVal if not found
 */
export function extractFilenameFromContentDisposition(response: HttpResponse<Blob>, defaultVal: string): string {
  // Get the Content-Disposition header (case-insensitive)
  const contentDisposition = response?.headers?.get('content-disposition');

  if (!contentDisposition) {
    return defaultVal;
  }

  // First try to extract RFC 5987 encoded filename (filename*)
  const filenameStarMatch = FILENAME_EXTRACTION_REGEX_RFC5987.exec(contentDisposition);
  if (filenameStarMatch?.[1]) {
    return decodeURIComponent(filenameStarMatch[1]);
  }

  // Fall back to regular filename
  const filenameMatch = FILENAME_EXTRACTION_REGEX_STANDARD.exec(contentDisposition);
  if (filenameMatch?.[1]) {
    return filenameMatch[1];
  }

  return defaultVal;
}

/**
 * Extracts the filename from the HTTP response.
 * This function first attempts to retrieve the filename from a custom 'file-name' header.
 * If the 'file-name' header is not present or empty, it then falls back to extracting
 * the filename from the standard 'Content-Disposition' header using the `extractFilenameFromContentDisposition` function.
 * The `defaultVal` is used as a final fallback if no filename can be extracted from either source.
 *
 * @param response The HTTP response containing potential filename headers.
 * @param defaultVal The default value to return if no filename can be extracted.
 * @returns The extracted filename, or `defaultVal` if no filename is found.
 */
export function extractFilename(response: HttpResponse<Blob>, defaultVal: string): string {
  // Attempt to get the filename from a custom 'file-name' header first.
  const fileName = response?.headers?.get('file-name');
  if (fileName?.length > 0) {
    return fileName;
  }

  // If the 'file-name' header is not found or empty, fall back to extracting
  // from the Content-Disposition header. The `defaultVal` will be used as a backup
  // if Content-Disposition also fails to provide a name.
  return extractFilenameFromContentDisposition(response, defaultVal);
}
